import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg, HOTEL_BRANDS } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Bridge, Buildings, Handshake } from "@phosphor-icons/react";

export default function Register() {
  const { register, homePath } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("company");
  const [f, setF] = useState({
    name: "", email: "", password: "", company_name: "", phone: "", address: "", industry: "", brand: "Independent",
  });
  const [customBrand, setCustomBrand] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const brand = f.brand === "Other (custom)" ? (customBrand || "Independent") : f.brand;
      const u = await register({ ...f, brand, email: f.email.trim(), role });
      toast.success(`Account created — welcome, ${u.name}`);
      navigate(homePath(u));
    } catch (err) {
      setError(errMsg(err, "Could not create account"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F3F4F6]">
      <div className="w-full max-w-lg bg-white border border-[#111827] p-8">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center"><Bridge size={20} weight="bold" color="#fff" /></div>
          <span className="font-display font-bold text-xl">RateBridge</span>
        </Link>
        <span className="overline text-[#002FA7]">Create account</span>
        <h1 className="font-display font-black text-3xl text-[#111827] mt-2">Join the marketplace.</h1>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <button type="button" data-testid="role-company" onClick={() => setRole("company")}
            className={`border p-4 text-left transition-colors ${role === "company" ? "border-[#002FA7] bg-[#E0E7FF]" : "border-[#E5E7EB] hover:border-[#111827]"}`}>
            <Buildings size={24} weight="duotone" color="#002FA7" />
            <div className="font-semibold text-[#111827] mt-2">Company</div>
            <div className="text-xs text-[#4B5563]">Search & negotiate hotels</div>
          </button>
          <button type="button" data-testid="role-gm" onClick={() => setRole("hotel_gm")}
            className={`border p-4 text-left transition-colors ${role === "hotel_gm" ? "border-[#002FA7] bg-[#E0E7FF]" : "border-[#E5E7EB] hover:border-[#111827]"}`}>
            <Handshake size={24} weight="duotone" color="#002FA7" />
            <div className="font-semibold text-[#111827] mt-2">Management Company / Group Admin</div>
            <div className="text-xs text-[#4B5563]">Add properties, team & sell</div>
          </button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Your name</span>
              <input data-testid="reg-name" required value={f.name} onChange={F("name")}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
            <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">{role === "company" ? "Company name" : "Group / brand"}</span>
              <input data-testid="reg-company" required value={f.company_name} onChange={F("company_name")}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          </div>
          <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Email</span>
            <input data-testid="reg-email" type="email" required value={f.email} onChange={F("email")}
              className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Password</span>
            <input data-testid="reg-password" type="password" required minLength={6} value={f.password} onChange={F("password")}
              className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Phone</span>
              <input data-testid="reg-phone" value={f.phone} onChange={F("phone")}
                className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
            {role === "company" && (
              <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Industry</span>
                <input data-testid="reg-industry" value={f.industry} onChange={F("industry")} placeholder="Construction, Energy…"
                  className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
            )}
            {role === "hotel_gm" && (
              <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Primary brand</span>
                <select data-testid="reg-brand" value={f.brand} onChange={F("brand")}
                  className="w-full border border-[#E5E7EB] px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  {HOTEL_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select></label>
            )}
          </div>
          {role === "hotel_gm" && f.brand === "Other (custom)" && (
            <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Custom brand name</span>
              <input data-testid="reg-brand-custom" value={customBrand} onChange={(e) => setCustomBrand(e.target.value)} placeholder="Your brand / flag"
                className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          )}
          <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">
            {role === "company" ? "Business address (for lead matching)" : "Head office address"}</span>
            <input data-testid="reg-address" value={f.address} onChange={F("address")} placeholder="Street, City, State"
              className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>

          {error && <div data-testid="register-error" className="bg-[#FEE2E2] text-[#B91C1C] text-sm px-3 py-2">{error}</div>}
          <button data-testid="register-submit" type="submit" disabled={loading}
            className="w-full bg-[#002FA7] text-white py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>
        <p className="text-sm text-[#4B5563] mt-5">
          Already have an account? <Link to="/login" data-testid="to-login" className="text-[#002FA7] font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
